/**
 * guideTopics.js - single source of truth for the Guides navigation.
 *
 * Guides are unified per-topic pages: each has a conceptual section, a portal-UI walkthrough,
 * and a "For Developers and Enterprise Integration" section covering API + MCP tools. This
 * one-topic-per-page model serves both non-technical prospects (who read the top of the page)
 * and developers (who jump to the "For Developers" section).
 *
 * Consumed by:
 *   - src/stores/aside.js (authenticated Dashboard sidebar - nests topics under "Guides")
 *   - src/components/common/AppSidebar.vue (renders the nested topics with sub-group labels)
 *   - Views/Docs/IntegrationGuides.vue (topic switcher; in public mode also renders the
 *     in-content GuideSidebar for visitors without a Dashboard sidebar)
 *
 * When adding a new topic:
 *   1. Add an entry under the appropriate group below (key, text, icon, component).
 *   2. Import + register the component in Views/Docs/IntegrationGuides.vue.
 *   3. No other files need to change - aside.js calls buildAsideGuideChildren() to pick up
 *      new topics automatically, and AppSidebar groups them by their `group` label.
 */

export const guideGroups = [
    {
        // Orientation content for any reader, developer or not. Both topics here read as
        // dual-audience - Overview frames the whole platform for everyone, and Quick Start
        // offers a portal path (no code) alongside an API path.
        key: 'getting-started',
        label: 'Getting Started',
        topics: [
            { key: 'overview',    text: 'Overview',    icon: 'rocket_launch', component: 'GuideGettingStarted' },
            { key: 'quick-start', text: 'Quick Start', icon: 'bolt',          component: 'GuideQuickStart' }
        ]
    },
    {
        // Developer prerequisites - things you must know before any API call will succeed.
        // Lives as its own group (rather than inside Getting Started) because it targets a
        // narrower audience than the orientation content. Future members: SDKs, API
        // versioning, idempotency conventions, etc.
        key: 'api-foundations',
        label: 'API Foundations',
        topics: [
            { key: 'authentication', text: 'Authentication & Scopes', icon: 'key', component: 'GuideAuthenticationScopes' }
        ]
    },
    {
        // Portal-side workspace administration - who can do what in the UI, and how the
        // workspace looks. Targets tenant admins onboarding their team and customizing the
        // signer-facing experience. Both topics have "For Developers" sections of their own
        // for the narrow subset of admin operations that are API-accessible.
        key: 'administration',
        label: 'Administration',
        topics: [
            { key: 'roles-permissions',    text: 'Roles & Permissions',    icon: 'admin_panel_settings', component: 'GuideRolesPermissions' },
            { key: 'enterprise-sso',       text: 'Enterprise SSO & SCIM',  icon: 'fingerprint',            component: 'GuideEnterpriseSso' },
            { key: 'subscription-limits', text: 'Seats & Usage Limits',   icon: 'speed',                  component: 'GuideSubscriptionLimits' },
            { key: 'branding',             text: 'Branding',               icon: 'palette',                component: 'GuideBranding' }
        ]
    },
    {
        // Data-model core: the objects every integration touches. Ordered by the container
        // hierarchy a reader builds up in their head - Packages hold Documents, Documents carry
        // Signing Fields (manually placed or auto-placed via Text Tags), and Signers complete
        // the fields. Text Tags sit next to Signing Fields because they are the field-placement
        // shortcut; a reader who just learned about fields expects "is there a faster way?" as
        // the very next topic.
        key: 'core-resources',
        label: 'Core Resources',
        topics: [
            { key: 'packages',    text: 'Packages',       icon: 'inventory_2', component: 'GuidePackages' },
            { key: 'documents',   text: 'Documents',      icon: 'description', component: 'GuideDocuments' },
            { key: 'annotations', text: 'Signing Fields', icon: 'draw',        component: 'GuideAnnotations' },
            { key: 'text-tags',   text: 'Text Tags',      icon: 'label',       component: 'GuideTextTags' },
            { key: 'signers',         text: 'Signers',                 icon: 'person',        component: 'GuideSigners' },
            { key: 'recipient-types', text: 'Recipient Types',          icon: 'badge',         component: 'GuideRecipientTypes' },
            { key: 'signer-auth',     text: 'Signer Authentication',    icon: 'verified_user', component: 'GuideSignerAuthentication' },
            { key: 'signer-payments', text: 'Signer Payments', icon: 'payments', component: 'GuideSignerPayments' },
            { key: 'sales-tax',       text: 'Sales Tax & VAT', icon: 'receipt',  component: 'GuideSalesTax' }
        ]
    },
    {
        // Set-up-once, reuse-everywhere primitives. Templates are reusable package setups;
        // Signing Groups are reusable recipient rosters. Renamed from "Authoring" because
        // these aren't authoring operations, they're reusable building blocks.
        key: 'building-blocks',
        label: 'Building Blocks',
        topics: [
            { key: 'templates',      text: 'Templates',      icon: 'content_copy', component: 'GuideTemplates' },
            { key: 'signing-groups', text: 'Signing Groups', icon: 'groups',       component: 'GuideSigningGroups' }
        ]
    },
    {
        // Ways your application talks to ours: embedding the ceremony in your own UI,
        // receiving server-to-server event pushes, and letting AI agents drive the platform
        // through the MCP server. Errors, rate limits, and evidence/compliance moved out to
        // their own group since they describe how the API behaves, not how you integrate.
        key: 'integration',
        label: 'Integration',
        topics: [
            { key: 'embedded-signing', text: 'Embedded Signing', icon: 'integration_instructions', component: 'GuideEmbeddedSigning' },
            { key: 'webhooks',         text: 'Webhooks',         icon: 'webhook',                   component: 'GuideWebhooks' },
            { key: 'hubspot',          text: 'HubSpot',          icon: 'hub',                       component: 'GuideHubspot' },
            { key: 'zapier',           text: 'Zapier',           icon: 'electric_bolt',             component: 'GuideZapier' },
            { key: 'make',             text: 'Make.com',         icon: 'account_tree',              component: 'GuideMake' },
            { key: 'ai-agents',        text: 'AI Agents',        icon: 'smart_toy',                 component: 'GuideAiAgents' }
        ]
    },
    {
        // How the API behaves operationally: failure modes (Errors & Rate Limits) and the
        // audit-trail guarantees it provides (Evidence & Compliance). These describe platform
        // behavior rather than integration patterns, so they stand apart from Integration.
        key: 'api-behavior',
        label: 'API Behavior',
        topics: [
            { key: 'errors',   text: 'Errors & Rate Limits',  icon: 'report_problem', component: 'GuideErrors' },
            { key: 'evidence', text: 'Evidence & Compliance', icon: 'verified',       component: 'GuideEvidence' }
        ]
    }
];

// Flat list used by the topic switcher to resolve ?topic=<key> to a component.
export const guideTopics = guideGroups.flatMap(g => g.topics);

// Default topic when no ?topic= query param is provided.
export const defaultGuideTopic = 'overview';

/**
 * Build sidebar children for the Dashboard's "Guides" expansion entry. Each child carries
 * a `group` label so AppSidebar can render small sub-group headers ("Getting Started",
 * "Core Resources", etc.) between the nested items, giving the Dashboard sidebar the same
 * grouped structure as the public-mode GuideSidebar in a more compact form.
 */
export function buildAsideGuideChildren() {
    return guideGroups.flatMap(group =>
        group.topics.map(t => ({
            icon: t.icon,
            text: t.text,
            viewkey: `docs_guides_${t.key}`,
            url: `/Docs/IntegrationGuides?topic=${t.key}`,
            group: group.label
        }))
    );
}

